import type { Product, Variant } from '@scayle/storefront-nuxt'

export function useVTOAvailability() {
  const isChecking = ref(false)
  const isAvailable = ref(false)
  const firstAvailableVariant = ref<Variant | undefined>()
  const firstAvailableEan = ref<string | undefined>()

  const getEan = (activeEan?: MaybeRefOrGetter<string | undefined>) => {
    return toValue(activeEan) || toValue(firstAvailableEan)
  }

  const checkFrameAvailability = useRpcCall('checkFrameAvailability')

  const fetchAvailability = async (product: Product) => {
    if (!product) {
      isAvailable.value = false
      firstAvailableVariant.value = undefined
      firstAvailableEan.value = undefined
      return false
    }

    isChecking.value = true

    try {
      // Check all variants of the product
      const variants = product.variants || []
      const variantsWithReferences = variants
        .map((variant) => ({
          variant,
          reference: getVirtualTryOnEan(variant),
        }))
        .filter(
          (item): item is { variant: Variant; reference: string } =>
            item.reference !== undefined,
        )

      if (variantsWithReferences.length === 0) {
        isAvailable.value = false
        firstAvailableVariant.value = undefined
        firstAvailableEan.value = undefined
        return false
      }

      const references = variantsWithReferences.map(
        ({ reference }) => reference,
      )

      const data = await checkFrameAvailability(references)

      if (!data) {
        return false
      }

      // Find the first available variant by matching the uid from the response
      const availableResponse = data?.find(
        (result: { available: boolean; uid: string }) => result.available,
      )
      if (availableResponse) {
        const availableIndex = variantsWithReferences.findIndex(
          ({ reference }) => reference === availableResponse.uid,
        )

        if (availableIndex !== -1) {
          firstAvailableVariant.value =
            variantsWithReferences[availableIndex].variant
          firstAvailableEan.value = availableResponse.uid
          isAvailable.value = true

          return true
        }
      }

      firstAvailableVariant.value = undefined
      firstAvailableEan.value = undefined
      isAvailable.value = false
      return false
    } catch (error) {
      console.error('Error checking frame availability:', error)
      isAvailable.value = false
      firstAvailableVariant.value = undefined
      firstAvailableEan.value = undefined
      return false
    } finally {
      isChecking.value = false
    }
  }

  return {
    isChecking,
    isAvailable,
    firstAvailableVariant,
    firstAvailableEan,
    fetchAvailability,
    getEan,
  }
}
